/**
 * Global Error Handler for ArbiWin API
 *
 * This module provides comprehensive error handling for all API interactions,
 * including HTTP status code processing, network failure detection, user-friendly
 * message generation, error logging, and recovery suggestions.
 *
 * Features:
 * - HTTP status code specific handling
 * - Network failure detection and retry logic
 * - User-friendly error message generation (Bulgarian/English)
 * - Error logging and reporting
 * - Authentication and subscription error handling
 * - Recovery suggestions and actions
 * - Error categorization and severity levels
 *
 * Following constitution requirements for error handling and user experience.
 */

import { HTTP_STATUS } from '../../../config/api';
import { logError } from '../../../utils/errorHandler';

/**
 * Error Categories for classification
 */
export const ERROR_CATEGORIES = {
  NETWORK: 'network',
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  SUBSCRIPTION: 'subscription',
  VALIDATION: 'validation',
  SERVER: 'server',
  CLIENT: 'client',
  TIMEOUT: 'timeout',
  RATE_LIMIT: 'rate_limit',
  MAINTENANCE: 'maintenance'
};

/**
 * Error Severity Levels
 */
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Recovery Actions for different error types
 */
export const RECOVERY_ACTIONS = {
  RETRY: 'retry',
  LOGIN: 'login',
  REFRESH_TOKEN: 'refresh_token',
  ACTIVATE_SUBSCRIPTION: 'activate_subscription',
  CONTACT_SUPPORT: 'contact_support',
  CHECK_NETWORK: 'check_network',
  WAIT_AND_RETRY: 'wait_and_retry',
  RELOAD_PAGE: 'reload_page',
  CLEAR_CACHE: 'clear_cache'
};

/**
 * Bilingual Error Messages
 * Following constitution requirements for Bulgarian/English support
 */
const ERROR_MESSAGES = {
  bg: {
    // Network errors
    [ERROR_CATEGORIES.NETWORK]: {
      title: 'Проблем с мрежата',
      message: 'Възникна проблем с връзката. Моля, проверете интернет връзката си.',
      action: 'Опитайте отново'
    },

    // Authentication errors
    [ERROR_CATEGORIES.AUTHENTICATION]: {
      title: 'Проблем с удостоверяването',
      message: 'Сесията ви е изтекла. Моля, влезте отново в системата.',
      action: 'Влезте отново'
    },

    // Authorization errors
    [ERROR_CATEGORIES.AUTHORIZATION]: {
      title: 'Няма достъп',
      message: 'Нямате право на достъп до тази функция.',
      action: 'Свържете се с поддръжката'
    },

    // Subscription errors
    [ERROR_CATEGORIES.SUBSCRIPTION]: {
      title: 'Необходим е абонамент',
      message: 'За достъп до тази функция е необходим активен абонамент.',
      action: 'Активирайте абонамент'
    },

    // Validation errors
    [ERROR_CATEGORIES.VALIDATION]: {
      title: 'Невалидни данни',
      message: 'Моля, проверете въведените данни и опитайте отново.',
      action: 'Поправете данните'
    },

    // Server errors
    [ERROR_CATEGORIES.SERVER]: {
      title: 'Проблем със сървъра',
      message: 'Възникна временен проблем със сървъра. Моля, опитайте по-късно.',
      action: 'Опитайте по-късно'
    },

    // Client errors
    [ERROR_CATEGORIES.CLIENT]: {
      title: 'Грешка в заявката',
      message: 'Заявката не може да бъде обработена.',
      action: 'Опитайте отново'
    },

    // Timeout errors
    [ERROR_CATEGORIES.TIMEOUT]: {
      title: 'Времето изтече',
      message: 'Заявката отне твърде много време. Моля, опитайте отново.',
      action: 'Опитайте отново'
    },

    // Rate limit errors
    [ERROR_CATEGORIES.RATE_LIMIT]: {
      title: 'Твърде много заявки',
      message: 'Направихте твърде много заявки. Моля, изчакайте преди да опитате отново.',
      action: 'Изчакайте и опитайте отново'
    },

    // Maintenance errors
    [ERROR_CATEGORIES.MAINTENANCE]: {
      title: 'Поддръжка на системата',
      message: 'Системата е временно недостъпна поради поддръжка.',
      action: 'Опитайте по-късно'
    }
  },

  en: {
    // Network errors
    [ERROR_CATEGORIES.NETWORK]: {
      title: 'Network Problem',
      message: 'A connection problem occurred. Please check your internet connection.',
      action: 'Try Again'
    },

    // Authentication errors
    [ERROR_CATEGORIES.AUTHENTICATION]: {
      title: 'Authentication Problem',
      message: 'Your session has expired. Please log in again.',
      action: 'Log In Again'
    },

    // Authorization errors
    [ERROR_CATEGORIES.AUTHORIZATION]: {
      title: 'Access Denied',
      message: 'You do not have permission to access this feature.',
      action: 'Contact Support'
    },

    // Subscription errors
    [ERROR_CATEGORIES.SUBSCRIPTION]: {
      title: 'Subscription Required',
      message: 'An active subscription is required to access this feature.',
      action: 'Activate Subscription'
    },

    // Validation errors
    [ERROR_CATEGORIES.VALIDATION]: {
      title: 'Invalid Data',
      message: 'Please check the entered data and try again.',
      action: 'Fix Data'
    },

    // Server errors
    [ERROR_CATEGORIES.SERVER]: {
      title: 'Server Problem',
      message: 'A temporary server problem occurred. Please try again later.',
      action: 'Try Later'
    },

    // Client errors
    [ERROR_CATEGORIES.CLIENT]: {
      title: 'Request Error',
      message: 'The request could not be processed.',
      action: 'Try Again'
    },

    // Timeout errors
    [ERROR_CATEGORIES.TIMEOUT]: {
      title: 'Timeout',
      message: 'The request took too long. Please try again.',
      action: 'Try Again'
    },

    // Rate limit errors
    [ERROR_CATEGORIES.RATE_LIMIT]: {
      title: 'Too Many Requests',
      message: 'You have made too many requests. Please wait before trying again.',
      action: 'Wait and Try Again'
    },

    // Maintenance errors
    [ERROR_CATEGORIES.MAINTENANCE]: {
      title: 'System Maintenance',
      message: 'The system is temporarily unavailable due to maintenance.',
      action: 'Try Later'
    }
  }
};

/**
 * HTTP Status Code to Error Category Mapping
 */
const HTTP_STATUS_MAPPING = {
  // 4xx Client Errors
  400: ERROR_CATEGORIES.VALIDATION,
  401: ERROR_CATEGORIES.AUTHENTICATION,
  402: ERROR_CATEGORIES.SUBSCRIPTION,
  403: ERROR_CATEGORIES.AUTHORIZATION,
  404: ERROR_CATEGORIES.CLIENT,
  408: ERROR_CATEGORIES.TIMEOUT,
  409: ERROR_CATEGORIES.VALIDATION,
  422: ERROR_CATEGORIES.VALIDATION,
  429: ERROR_CATEGORIES.RATE_LIMIT,

  // 5xx Server Errors
  500: ERROR_CATEGORIES.SERVER,
  501: ERROR_CATEGORIES.SERVER,
  502: ERROR_CATEGORIES.SERVER,
  503: ERROR_CATEGORIES.MAINTENANCE,
  504: ERROR_CATEGORIES.TIMEOUT,
  505: ERROR_CATEGORIES.SERVER
};

/**
 * Network Error Detection Patterns
 */
const NETWORK_ERROR_PATTERNS = [
  'NETWORK_ERROR',
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
  'ERR_CONNECTION_REFUSED',
  'ERR_CONNECTION_RESET',
  'ERR_CONNECTION_ABORTED',
  'ERR_NAME_NOT_RESOLVED',
  'ERR_CERT_AUTHORITY_INVALID',
  'ENOTFOUND',
  'ECONNREFUSED',
  'ECONNRESET',
  'ETIMEDOUT'
];

/**
 * Global Error Handler Class
 */
export class GlobalErrorHandler {
  constructor(options = {}) {
    this.language = options.language || 'bg';
    this.enableLogging = options.enableLogging !== false;
    this.enableReporting = options.enableReporting || false;
    this.retryConfig = options.retryConfig || {
      maxRetries: 3,
      baseDelay: 1000,
      maxDelay: 10000,
      backoffFactor: 2
    };

    // Error event listeners
    this.errorListeners = [];
    this.recoveryListeners = [];

    // Error statistics
    this.errorStats = {
      total: 0,
      byCategory: {},
      byStatus: {},
      lastError: null
    };
  }

  /**
   * Process and handle an API error
   * @param {Error|Object} error - Error object from API call
   * @param {Object} context - Additional context information
   * @returns {Object} Processed error information
   */
  handleError(error, context = {}) {
    try {
      // Extract error information
      const errorInfo = this.extractErrorInfo(error, context);

      // Categorize the error
      const category = this.categorizeError(errorInfo);

      // Determine severity
      const severity = this.determineSeverity(errorInfo, category);

      // Generate user-friendly message
      const userMessage = this.generateUserMessage(category, errorInfo);

      // Determine recovery actions
      const recoveryActions = this.determineRecoveryActions(category, errorInfo);

      // Create processed error object
      const processedError = {
        id: this.generateErrorId(),
        timestamp: new Date().toISOString(),
        category: category,
        severity: severity,
        originalError: errorInfo,
        userMessage: userMessage,
        recoveryActions: recoveryActions,
        context: context,
        retryable: this.isRetryable(category, errorInfo),
        shouldReport: this.shouldReport(category, severity)
      };

      // Update statistics
      this.updateErrorStats(processedError);

      // Log the error
      if (this.enableLogging) {
        this.logError(processedError);
      }

      // Report the error if needed
      if (this.enableReporting && processedError.shouldReport) {
        this.reportError(processedError);
      }

      // Notify listeners
      this.notifyErrorListeners(processedError);

      // Handle specific error types
      this.handleSpecificErrors(processedError);

      return processedError;

    } catch (handlingError) {
      // Fallback error handling
      console.error('Error in error handler:', handlingError);
      return this.createFallbackError(error, context);
    }
  }

  /**
   * Extract error information from various error types
   * @param {Error|Object} error - Error object
   * @param {Object} context - Context information
   * @returns {Object} Extracted error information
   */
  extractErrorInfo(error, context) {
    const errorInfo = {
      message: '',
      status: null,
      statusText: '',
      data: null,
      headers: {},
      config: {},
      isAxiosError: false,
      isNetworkError: false,
      isTimeoutError: false,
      code: null
    };

    if (error.isAxiosError) {
      // Axios error
      errorInfo.isAxiosError = true;
      errorInfo.message = error.message;
      errorInfo.code = error.code;

      if (error.response) {
        // Server responded with error status
        errorInfo.status = error.response.status;
        errorInfo.statusText = error.response.statusText;
        errorInfo.data = error.response.data;
        errorInfo.headers = error.response.headers;
      } else if (error.request) {
        // Request made but no response
        errorInfo.isNetworkError = true;
        errorInfo.message = 'No response received from server';
      }

      errorInfo.config = error.config || {};

      // Check for timeout
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorInfo.isTimeoutError = true;
      }

    } else if (error instanceof Error) {
      // Regular JavaScript error
      errorInfo.message = error.message;
      errorInfo.code = error.name;

      // Check if it's a network error
      errorInfo.isNetworkError = this.isNetworkError(error);

    } else if (typeof error === 'object') {
      // Custom error object
      Object.assign(errorInfo, error);
    } else {
      // String or other type
      errorInfo.message = String(error);
    }

    return errorInfo;
  }

  /**
   * Categorize error based on error information
   * @param {Object} errorInfo - Extracted error information
   * @returns {string} Error category
   */
  categorizeError(errorInfo) {
    // Network errors
    if (errorInfo.isNetworkError || this.isNetworkError(errorInfo)) {
      return ERROR_CATEGORIES.NETWORK;
    }

    // Timeout errors
    if (errorInfo.isTimeoutError) {
      return ERROR_CATEGORIES.TIMEOUT;
    }

    // HTTP status code based categorization
    if (errorInfo.status && HTTP_STATUS_MAPPING[errorInfo.status]) {
      return HTTP_STATUS_MAPPING[errorInfo.status];
    }

    // Message based categorization
    const message = (errorInfo.message || '').toLowerCase();

    if (message.includes('network') || message.includes('connection')) {
      return ERROR_CATEGORIES.NETWORK;
    }

    if (message.includes('timeout')) {
      return ERROR_CATEGORIES.TIMEOUT;
    }

    if (message.includes('unauthorized') || message.includes('authentication')) {
      return ERROR_CATEGORIES.AUTHENTICATION;
    }

    if (message.includes('forbidden') || message.includes('permission')) {
      return ERROR_CATEGORIES.AUTHORIZATION;
    }

    if (message.includes('subscription') || message.includes('payment')) {
      return ERROR_CATEGORIES.SUBSCRIPTION;
    }

    if (message.includes('validation') || message.includes('invalid')) {
      return ERROR_CATEGORIES.VALIDATION;
    }

    // Default categorization
    if (errorInfo.status >= 500) {
      return ERROR_CATEGORIES.SERVER;
    } else if (errorInfo.status >= 400) {
      return ERROR_CATEGORIES.CLIENT;
    }

    return ERROR_CATEGORIES.CLIENT;
  }

  /**
   * Determine error severity
   * @param {Object} errorInfo - Error information
   * @param {string} category - Error category
   * @returns {string} Error severity
   */
  determineSeverity(errorInfo, category) {
    // Critical errors
    if (category === ERROR_CATEGORIES.SERVER && errorInfo.status >= 500) {
      return ERROR_SEVERITY.CRITICAL;
    }

    if (category === ERROR_CATEGORIES.NETWORK && !navigator.onLine) {
      return ERROR_SEVERITY.CRITICAL;
    }

    // High severity errors
    if (category === ERROR_CATEGORIES.AUTHENTICATION) {
      return ERROR_SEVERITY.HIGH;
    }

    if (category === ERROR_CATEGORIES.SUBSCRIPTION) {
      return ERROR_SEVERITY.HIGH;
    }

    // Medium severity errors
    if (category === ERROR_CATEGORIES.AUTHORIZATION) {
      return ERROR_SEVERITY.MEDIUM;
    }

    if (category === ERROR_CATEGORIES.VALIDATION) {
      return ERROR_SEVERITY.MEDIUM;
    }

    if (category === ERROR_CATEGORIES.RATE_LIMIT) {
      return ERROR_SEVERITY.MEDIUM;
    }

    // Low severity errors
    return ERROR_SEVERITY.LOW;
  }

  /**
   * Generate user-friendly error message
   * @param {string} category - Error category
   * @param {Object} errorInfo - Error information
   * @returns {Object} User message object
   */
  generateUserMessage(category, errorInfo) {
    const messages = ERROR_MESSAGES[this.language] || ERROR_MESSAGES.bg;
    const categoryMessage = messages[category] || messages[ERROR_CATEGORIES.CLIENT];

    let customMessage = categoryMessage.message;
    const customAction = categoryMessage.action;

    // Customize message based on specific error details
    if (category === ERROR_CATEGORIES.VALIDATION && errorInfo.data && errorInfo.data.validationErrors) {
      const validationErrors = errorInfo.data.validationErrors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        customMessage = validationErrors.join(', ');
      }
    }

    if (category === ERROR_CATEGORIES.RATE_LIMIT && errorInfo.headers && errorInfo.headers['retry-after']) {
      const retryAfter = parseInt(errorInfo.headers['retry-after'], 10);
      if (this.language === 'bg') {
        customMessage = `Твърде много заявки. Опитайте отново след ${retryAfter} секунди.`;
      } else {
        customMessage = `Too many requests. Try again in ${retryAfter} seconds.`;
      }
    }

    return {
      title: categoryMessage.title,
      message: customMessage,
      action: customAction,
      details: errorInfo.data ? errorInfo.data.message : null
    };
  }

  /**
   * Determine recovery actions for the error
   * @param {string} category - Error category
   * @param {Object} errorInfo - Error information
   * @returns {Array} Array of recovery actions
   */
  determineRecoveryActions(category, errorInfo) {
    const actions = [];

    switch (category) {
      case ERROR_CATEGORIES.NETWORK:
        actions.push(RECOVERY_ACTIONS.CHECK_NETWORK);
        actions.push(RECOVERY_ACTIONS.RETRY);
        break;

      case ERROR_CATEGORIES.AUTHENTICATION:
        actions.push(RECOVERY_ACTIONS.REFRESH_TOKEN);
        actions.push(RECOVERY_ACTIONS.LOGIN);
        break;

      case ERROR_CATEGORIES.AUTHORIZATION:
        actions.push(RECOVERY_ACTIONS.CONTACT_SUPPORT);
        break;

      case ERROR_CATEGORIES.SUBSCRIPTION:
        actions.push(RECOVERY_ACTIONS.ACTIVATE_SUBSCRIPTION);
        break;

      case ERROR_CATEGORIES.VALIDATION:
        actions.push(RECOVERY_ACTIONS.RETRY);
        break;

      case ERROR_CATEGORIES.SERVER:
        actions.push(RECOVERY_ACTIONS.WAIT_AND_RETRY);
        actions.push(RECOVERY_ACTIONS.CONTACT_SUPPORT);
        break;

      case ERROR_CATEGORIES.TIMEOUT:
        actions.push(RECOVERY_ACTIONS.RETRY);
        break;

      case ERROR_CATEGORIES.RATE_LIMIT:
        actions.push(RECOVERY_ACTIONS.WAIT_AND_RETRY);
        break;

      case ERROR_CATEGORIES.MAINTENANCE:
        actions.push(RECOVERY_ACTIONS.WAIT_AND_RETRY);
        break;

      default:
        actions.push(RECOVERY_ACTIONS.RETRY);
        actions.push(RECOVERY_ACTIONS.RELOAD_PAGE);
    }

    return actions;
  }

  /**
   * Check if error is network-related
   * @param {Object} error - Error object
   * @returns {boolean} True if network error
   */
  isNetworkError(error) {
    const message = (error.message || error.code || '').toUpperCase();
    return NETWORK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
  }

  /**
   * Check if error is retryable
   * @param {string} category - Error category
   * @param {Object} errorInfo - Error information
   * @returns {boolean} True if retryable
   */
  isRetryable(category, errorInfo) {
    // Non-retryable categories
    const nonRetryableCategories = [
      ERROR_CATEGORIES.AUTHENTICATION,
      ERROR_CATEGORIES.AUTHORIZATION,
      ERROR_CATEGORIES.VALIDATION
    ];

    if (nonRetryableCategories.includes(category)) {
      return false;
    }

    // Non-retryable status codes
    const nonRetryableStatus = [400, 401, 403, 404, 422];
    if (nonRetryableStatus.includes(errorInfo.status)) {
      return false;
    }

    return true;
  }

  /**
   * Check if error should be reported
   * @param {string} category - Error category
   * @param {string} severity - Error severity
   * @returns {boolean} True if should report
   */
  shouldReport(category, severity) {
    // Always report critical errors
    if (severity === ERROR_SEVERITY.CRITICAL) {
      return true;
    }

    // Report server errors
    if (category === ERROR_CATEGORIES.SERVER) {
      return true;
    }

    // Don't report client validation errors
    if (category === ERROR_CATEGORIES.VALIDATION) {
      return false;
    }

    // Report high severity errors
    return severity === ERROR_SEVERITY.HIGH;
  }

  /**
   * Handle specific error types with custom logic
   * @param {Object} processedError - Processed error object
   */
  handleSpecificErrors(processedError) {
    const { category, originalError } = processedError;

    // Handle authentication errors
    if (category === ERROR_CATEGORIES.AUTHENTICATION) {
      // Dispatch authentication error event
      window.dispatchEvent(new CustomEvent('auth:tokenExpired', {
        detail: { error: processedError }
      }));
    }

    // Handle subscription errors (HTTP 402)
    if (category === ERROR_CATEGORIES.SUBSCRIPTION) {
      // Check if this is an authentication endpoint
      const endpoint = originalError.config?.url;
      const authEndpoints = [
        '/auth/login',
        '/auth/register',
        '/auth/forgot-password',
        '/auth/reset-password',
        '/auth/logout',
        '/auth/refresh-token',
        '/auth/validate-session'
      ];
      
      const isAuthEndpoint = authEndpoints.some(authEndpoint => 
        endpoint && endpoint.includes(authEndpoint)
      );
      
      // Only dispatch subscription required event for non-auth endpoints
      if (!isAuthEndpoint) {
        // Dispatch subscription required event
        window.dispatchEvent(new CustomEvent('subscription:required', {
          detail: { error: processedError }
        }));
      }
    }

    // Handle rate limiting
    if (category === ERROR_CATEGORIES.RATE_LIMIT) {
      const retryAfter = originalError.headers && originalError.headers['retry-after'];
      if (retryAfter) {
        // Dispatch rate limit event with retry time
        window.dispatchEvent(new CustomEvent('api:rateLimited', {
          detail: {
            error: processedError,
            retryAfter: parseInt(retryAfter, 10) * 1000 // Convert to milliseconds
          }
        }));
      }
    }
  }

  /**
   * Update error statistics
   * @param {Object} processedError - Processed error object
   */
  updateErrorStats(processedError) {
    this.errorStats.total++;
    this.errorStats.lastError = processedError;

    // Update category stats
    if (!this.errorStats.byCategory[processedError.category]) {
      this.errorStats.byCategory[processedError.category] = 0;
    }
    this.errorStats.byCategory[processedError.category]++;

    // Update status stats
    if (processedError.originalError.status) {
      const status = processedError.originalError.status;
      if (!this.errorStats.byStatus[status]) {
        this.errorStats.byStatus[status] = 0;
      }
      this.errorStats.byStatus[status]++;
    }
  }

  /**
   * Log error to console and external logging service
   * @param {Object} processedError - Processed error object
   */
  logError(processedError) {
    const logData = {
      id: processedError.id,
      timestamp: processedError.timestamp,
      category: processedError.category,
      severity: processedError.severity,
      message: processedError.originalError.message,
      status: processedError.originalError.status,
      url: processedError.context.url,
      userAgent: navigator.userAgent,
      language: this.language
    };

    // Console logging based on severity
    switch (processedError.severity) {
      case ERROR_SEVERITY.CRITICAL:
        console.error('🔴 CRITICAL ERROR:', logData);
        break;
      case ERROR_SEVERITY.HIGH:
        console.error('🟠 HIGH SEVERITY ERROR:', logData);
        break;
      case ERROR_SEVERITY.MEDIUM:
        console.warn('🟡 MEDIUM SEVERITY ERROR:', logData);
        break;
      default:
        console.log('🔵 ERROR:', logData);
    }

    // Use existing error handler for additional logging
    logError(processedError.originalError, processedError.context);
  }

  /**
   * Report error to external service
   * @param {Object} processedError - Processed error object
   */
  reportError(processedError) {
    // This would integrate with external error reporting service
    // For now, we'll just log it
    console.log('📊 REPORTING ERROR:', {
      id: processedError.id,
      category: processedError.category,
      severity: processedError.severity,
      timestamp: processedError.timestamp
    });
  }

  /**
   * Generate unique error ID
   * @returns {string} Unique error ID
   */
  generateErrorId() {
    return 'err_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Create fallback error when error handler fails
   * @param {*} originalError - Original error
   * @param {Object} context - Context information
   * @returns {Object} Fallback error object
   */
  createFallbackError(originalError, context) {
    return {
      id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      category: ERROR_CATEGORIES.CLIENT,
      severity: ERROR_SEVERITY.MEDIUM,
      originalError: { message: String(originalError) },
      userMessage: {
        title: this.language === 'bg' ? 'Неочаквана грешка' : 'Unexpected Error',
        message: this.language === 'bg' ? 'Възникна неочаквана грешка.' : 'An unexpected error occurred.',
        action: this.language === 'bg' ? 'Опитайте отново' : 'Try Again'
      },
      recoveryActions: [RECOVERY_ACTIONS.RETRY, RECOVERY_ACTIONS.RELOAD_PAGE],
      context: context,
      retryable: true,
      shouldReport: true
    };
  }

  /**
   * Add error event listener
   * @param {Function} listener - Error event listener
   */
  onError(listener) {
    this.errorListeners.push(listener);
  }

  /**
   * Add recovery event listener
   * @param {Function} listener - Recovery event listener
   */
  onRecovery(listener) {
    this.recoveryListeners.push(listener);
  }

  /**
   * Notify error listeners
   * @param {Object} processedError - Processed error object
   */
  notifyErrorListeners(processedError) {
    this.errorListeners.forEach(listener => {
      try {
        listener(processedError);
      } catch (error) {
        console.error('Error in error listener:', error);
      }
    });
  }

  /**
   * Get error statistics
   * @returns {Object} Error statistics
   */
  getErrorStats() {
    return { ...this.errorStats };
  }

  /**
   * Clear error statistics
   */
  clearErrorStats() {
    this.errorStats = {
      total: 0,
      byCategory: {},
      byStatus: {},
      lastError: null
    };
  }

  /**
   * Set language for error messages
   * @param {string} language - Language code ('bg' or 'en')
   */
  setLanguage(language) {
    this.language = language;
  }
}

/**
 * Create global error handler instance
 */
export const globalErrorHandler = new GlobalErrorHandler({
  language: 'bg', // Default to Bulgarian
  enableLogging: true,
  enableReporting: process.env.NODE_ENV === 'production'
});

/**
 * Convenience functions for error handling
 */
export const handleApiError = (error, context = {}) => {
  return globalErrorHandler.handleError(error, context);
};

export const handleNetworkError = (error, context = {}) => {
  return globalErrorHandler.handleError(error, {
    ...context,
    type: 'network'
  });
};

export const handleAuthError = (error, context = {}) => {
  return globalErrorHandler.handleError(error, {
    ...context,
    type: 'authentication'
  });
};

export const handleSubscriptionError = (error, context = {}) => {
  return globalErrorHandler.handleError(error, {
    ...context,
    type: 'subscription'
  });
};

/**
 * Retry utility with exponential backoff
 */
export class RetryHandler {
  constructor(options = {}) {
    this.maxRetries = options.maxRetries || 3;
    this.baseDelay = options.baseDelay || 1000;
    this.maxDelay = options.maxDelay || 10000;
    this.backoffFactor = options.backoffFactor || 2;
    this.jitter = options.jitter !== false;
  }

  /**
   * Execute function with retry logic
   * @param {Function} fn - Function to execute
   * @param {Object} context - Context for error handling
   * @returns {Promise} Promise that resolves with function result
   */
  async execute(fn, context = {}) {
    let lastError;

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const result = await fn();

        // Success - notify recovery if this was a retry
        if (attempt > 0) {
          globalErrorHandler.recoveryListeners.forEach(listener => {
            try {
              listener({
                attempt,
                totalAttempts: this.maxRetries + 1,
                context
              });
            } catch (error) {
              console.error('Error in recovery listener:', error);
            }
          });
        }

        return result;

      } catch (error) {
        lastError = error;

        // Process error
        const processedError = globalErrorHandler.handleError(error, {
          ...context,
          attempt,
          maxRetries: this.maxRetries
        });

        // Don't retry if error is not retryable
        if (!processedError.retryable) {
          throw processedError;
        }

        // Don't retry on last attempt
        if (attempt === this.maxRetries) {
          throw processedError;
        }

        // Calculate delay for next attempt
        const delay = this.calculateDelay(attempt);

        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // This should never be reached, but just in case
    throw globalErrorHandler.handleError(lastError, context);
  }

  /**
   * Calculate delay for retry attempt
   * @param {number} attempt - Current attempt number
   * @returns {number} Delay in milliseconds
   */
  calculateDelay(attempt) {
    let delay = this.baseDelay * Math.pow(this.backoffFactor, attempt);

    // Apply maximum delay limit
    delay = Math.min(delay, this.maxDelay);

    // Add jitter to prevent thundering herd
    if (this.jitter) {
      delay = delay * (0.5 + Math.random() * 0.5);
    }

    return Math.floor(delay);
  }
}

/**
 * Create retry handler instance
 */
export const retryHandler = new RetryHandler();

/**
 * Export error handling utilities
 */
export const errorUtils = {
  handle: handleApiError,
  handleNetwork: handleNetworkError,
  handleAuth: handleAuthError,
  handleSubscription: handleSubscriptionError,
  retry: (fn, context) => retryHandler.execute(fn, context),
  getStats: () => globalErrorHandler.getErrorStats(),
  clearStats: () => globalErrorHandler.clearErrorStats(),
  setLanguage: (language) => globalErrorHandler.setLanguage(language)
};

export default {
  GlobalErrorHandler,
  RetryHandler,
  globalErrorHandler,
  retryHandler,
  errorUtils,
  handleApiError,
  handleNetworkError,
  handleAuthError,
  handleSubscriptionError,
  ERROR_CATEGORIES,
  ERROR_SEVERITY,
  RECOVERY_ACTIONS
};
